function initScrollytelling() {
  const canvas = document.querySelector('.scrollytelling-canvas');
  if (!canvas) return;

  if (canvas.__scrollytellingController) {
    canvas.__scrollytellingController.teardown();
  }

  const DEBUG_SCROLLYTELLING = false;
  const debugLog = (...args) => {
    if (DEBUG_SCROLLYTELLING) console.log('[scrollytelling]', ...args);
  };

  if (!window.THREE || !window.gsap || !window.ScrollTrigger || !THREE.GLTFLoader || !THREE.RoomEnvironment) {
    debugLog('Required Three.js or GSAP dependencies are missing');
    canvas.classList.remove('is-ready', 'is-loading');
    canvas.classList.add('is-recovering');
    return;
  }

  gsap.registerPlugin(ScrollTrigger);

  const MOBILE_CANVAS_HEIGHT_BUFFER = 0.15;
  const MOBILE_ZOOM_STRENGTH = 0.2;
  const MOBILE_EXTRA_ZOOM = 0;
  const DESIGN_ASPECT = 16 / 9;
  const HERO_LIGHT_INTENSITY = 3.75;
  const MODEL_COLOR_SCROLLED = 0x919191;
  const MODEL_COLOR_HERO = 0x565656;
  const MODEL_OPACITY_HERO = 1;
  const MODEL_OPACITY_SCROLLED = 1.0;

  let scene;
  let camera;
  let renderer;
  let pmrem;
  let studioEnv;
  let activeModel;
  let directionalLight;
  let heroLight;
  let bgMaterial;
  let keySprite;
  let jointSprite;
  let timeline;
  let animationFrameId;
  let resizeTimer;
  let lastResizeWidth = window.innerWidth;
  let lastResizeHeight = window.innerHeight;
  let isDisposed = false;
  let isContextLost = false;
  const modelMaterials = [];
  const cleanupFns = [];

  canvas.__scrollytellingController = { teardown };
  canvas.classList.remove('is-ready', 'is-faded-out', 'is-recovering');
  canvas.classList.add('is-loading');

  function canvasHeight() {
    const isMobile = window.innerWidth < 750;
    return window.innerHeight * (isMobile ? 1 + MOBILE_CANVAS_HEIGHT_BUFFER : 1);
  }

  function normalizeAssetUrl(url) {
    if (!url) return '';
    return url.startsWith('//') ? `https:${url}` : url;
  }

  function loadTexture(url, loader) {
    if (!url) return Promise.resolve(null);

    return new Promise((resolve) => {
      loader.load(
        normalizeAssetUrl(url),
        (texture) => resolve(texture),
        undefined,
        (error) => {
          debugLog('Optional texture failed to load', url, error);
          resolve(null);
        }
      );
    });
  }

  function loadModel(url) {
    return new Promise((resolve, reject) => {
      if (!url) {
        reject(new Error('Missing scrollytelling model URL'));
        return;
      }

      new THREE.GLTFLoader().load(normalizeAssetUrl(url), resolve, undefined, reject);
    });
  }

  async function loadAssets() {
    const textureLoader = new THREE.TextureLoader();
    textureLoader.setCrossOrigin('anonymous');

    const textureResults = await Promise.allSettled([
      loadTexture(canvas.getAttribute('data-key-image-url'), textureLoader),
      loadTexture(canvas.getAttribute('data-joint-url'), textureLoader)
    ]);

    const gltf = await loadModel(canvas.getAttribute('data-model-url'));

    return {
      gltf,
      keyTexture: textureResults[0].status === 'fulfilled' ? textureResults[0].value : null,
      jointTexture: textureResults[1].status === 'fulfilled' ? textureResults[1].value : null
    };
  }

  function createScene(assets) {
    scene = new THREE.Scene();
    camera = new THREE.PerspectiveCamera(75, window.innerWidth / canvasHeight(), 0.1, 1000);
    camera.position.z = 5;

    renderer = new THREE.WebGLRenderer({
      canvas,
      alpha: true,
      antialias: true,
      powerPreference: 'high-performance'
    });
    renderer.setClearColor(0x000000, 0);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(window.innerWidth, canvasHeight());

    pmrem = new THREE.PMREMGenerator(renderer);
    studioEnv = pmrem.fromScene(new THREE.RoomEnvironment(), 0.04).texture;

    directionalLight = new THREE.PointLight(0xffffff, 1.2);
    directionalLight.position.set(100, 0, 3);
    scene.add(directionalLight);

    heroLight = new THREE.DirectionalLight(0xffffff, HERO_LIGHT_INTENSITY);
    heroLight.position.set(-12, 10, 3);
    scene.add(heroLight);

    bgMaterial = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      roughness: 0.8,
      metalness: 0.1,
      transparent: true,
      opacity: 0
    });
    const bgMesh = new THREE.Mesh(new THREE.PlaneGeometry(200, 200), bgMaterial);
    scene.add(bgMesh);

    keySprite = new THREE.Sprite(new THREE.SpriteMaterial({
      map: assets.keyTexture,
      color: 0xffffff,
      transparent: true,
      opacity: 0,
      depthWrite: false
    }));
    keySprite.visible = Boolean(assets.keyTexture);
    keySprite.scale.set(2, 1.5, 1);
    keySprite.position.set(2, 0.3, 3);
    scene.add(keySprite);

    jointSprite = new THREE.Sprite(new THREE.SpriteMaterial({
      map: assets.jointTexture,
      color: 0xffffff,
      transparent: true,
      opacity: 0,
      depthWrite: false
    }));
    jointSprite.visible = Boolean(assets.jointTexture);
    jointSprite.scale.set(1.1, 2.6, 1);
    jointSprite.position.set(0, -2.5, 3);
    scene.add(jointSprite);

    activeModel = new THREE.Group();
    activeModel.add(assets.gltf.scene);
    scene.add(activeModel);

    activeModel.traverse((child) => {
      if (!child.isMesh) return;

      child.material.transparent = true;
      child.material.opacity = MODEL_OPACITY_HERO;
      child.material.color.setHex(MODEL_COLOR_HERO);
      child.material.metalness = 1.0;
      child.material.roughness = 0.2;
      child.material.map = null;
      child.material.metalnessMap = null;
      child.material.roughnessMap = null;
      child.material.envMap = studioEnv;
      child.material.envMapIntensity = 1.0;
      child.material.needsUpdate = true;
      modelMaterials.push(child.material);
    });
  }

  function setupAnimations() {
    bgMaterial.opacity = 0;
    heroLight.intensity = HERO_LIGHT_INTENSITY;

    const KEYFRAMES_DESKTOP = {
      home: {
        model: { pos: {x: 1.5, y: -0.3, z: 2.3}, rot: {x: 0, y: 0, z: -Math.PI/4.5} },
      },
      feature1: {
        model:     { pos: {x: 1.5, y: -1.4, z: 3}, rot: {x: 0, y: -Math.PI, z: -0.4} },
        keySprite: { pos: {x: 1.45, y: 0.3, z: 3.2}, scale: {x: 2, y: 1.5, z: 1} },
      },
      feature2: {
        model:       { pos: {x: 0, y: 1, z: 2}, rot: {x: 0, y: Math.PI/8, z: 0} },
        keySprite:   { pos: {x: 2, y: 0.3, z: 1} },
        jointSprite: { pos: {x: 0, y: -1.4, z: 3.2} },
      },
      stopfen_runter: {
        model: { pos: {x: 0, y: 0, z: 2} },
      },
      stopfen_hoch: {
        model:       { pos: {x: 0, y: 1, z: 2} },
        jointSprite: { pos: {x: 0, y: -2, z: 3.2} },
      },
    };

    const KEYFRAMES_MOBILE = {
      home: {
        model: { pos: {x: 0, y: -2.5, z: 2.7}, rot: {x: 0, y: 0, z: 0} },
      },
      feature1: {
        model:     { pos: {x: 0.2, y: -1.4, z: 3}, rot: {x: 0, y: -Math.PI, z: -0.4} },
        keySprite: { pos: {x: 0.25, y: 0.3, z: 3.2}, scale: {x: 2, y: 1.5, z: 1} },
      },
      feature2: {
        model:       { pos: {x: 0, y: 1, z: 2}, rot: {x: 0, y: Math.PI/8, z: 0} },
        keySprite:   { pos: {x: 3.1, y: 0.3, z: 1} },
        jointSprite: { pos: {x: 0, y: -1.4, z: 3.2} },
      },
      stopfen_runter: {
        model: { pos: {x: 0, y: 0, z: 2} },
      },
      stopfen_hoch: {
        model:       { pos: {x: 0, y: 1, z: 2} },
        jointSprite: { pos: {x: 0, y: -2, z: 3.2} },
      },
    };

    const isMobileLayout = window.innerWidth < 750;
    const KF = isMobileLayout ? KEYFRAMES_MOBILE : KEYFRAMES_DESKTOP;

    activeModel.position.set(KF.home.model.pos.x, KF.home.model.pos.y, KF.home.model.pos.z);
    activeModel.rotation.set(KF.home.model.rot.x, KF.home.model.rot.y, KF.home.model.rot.z);

    timeline = gsap.timeline({
      scrollTrigger: {
        trigger: '.scrollytelling-container',
        start: 'top top',
        end: 'bottom bottom',
        scrub: 1
      }
    });

    timeline.add('feature1', 0);
    timeline.add('feature2', 1);
    timeline.add('stopfen_runter', 2);
    timeline.add('stopfen_hoch', 2.2);

    timeline.to(activeModel.position, { ...KF.feature1.model.pos, duration: 1 }, 'feature1')
      .to(activeModel.rotation, { ...KF.feature1.model.rot, duration: 1 }, 'feature1')
      .to(bgMaterial, { opacity: 1, duration: 1 }, 'feature1')
      .to(keySprite.material, { opacity: keySprite.visible ? 0.7 : 0, duration: 0.5, ease: 'power3.in' }, 'feature1')
      .to(keySprite.position, { ...KF.feature1.keySprite.pos, duration: 1 }, 'feature1')
      .to(keySprite.scale,    { ...KF.feature1.keySprite.scale, duration: 1 }, 'feature1');

    timeline.to(activeModel.position, { ...KF.feature2.model.pos, duration: 0.75 }, 'feature2')
      .to(activeModel.rotation, { ...KF.feature2.model.rot, duration: 0.75 }, 'feature2')
      .to(bgMaterial, { opacity: 1, duration: 1 }, 'feature2')
      .to(keySprite.material, { opacity: 0, duration: 0.5, ease: 'power3.out' }, 'feature2')
      .to(keySprite.position, { ...KF.feature2.keySprite.pos, duration: 1 }, 'feature2')
      .to(jointSprite.material, { opacity: jointSprite.visible ? 0.7 : 0, duration: 0.5, ease: 'power3.in' }, 'feature2')
      .to(jointSprite.position, { ...KF.feature2.jointSprite.pos, duration: 0.5 }, 'feature2');

    timeline.to(activeModel.position, { ...KF.stopfen_runter.model.pos, duration: 0.2 }, 'stopfen_runter');
    timeline.to(activeModel.position, { ...KF.stopfen_hoch.model.pos, duration: 0.2 }, 'stopfen_hoch')
      .to(jointSprite.material, { opacity: 0, duration: 0.5, ease: 'power3.in' }, 'stopfen_hoch')
      .to(jointSprite.position, { ...KF.stopfen_hoch.jointSprite.pos, duration: 0.5 }, 'stopfen_hoch');

    const col1 = new THREE.Color('#ff4800');
    const col2 = new THREE.Color('#358f3b');

    timeline.to(heroLight, { intensity: 0, duration: 0.5 }, 'feature1');
    timeline.to(directionalLight.color, { r: col1.r, g: col1.g, b: col1.b, duration: 1 }, 'feature1')
      .to(directionalLight.position, { x: 2, y: 2, z: 3 }, 'feature1')
      .to(directionalLight.color, { r: col2.r, g: col2.g, b: col2.b, duration: 1 }, 'feature2')
      .to(directionalLight.position, { x: -2, y: -2, z: 3 }, 'feature2');

    const heroCol = new THREE.Color(MODEL_COLOR_HERO);
    const scrolledCol = new THREE.Color(MODEL_COLOR_SCROLLED);
    const tmpCol = new THREE.Color();
    const brightness = { t: 0 };
    timeline.to(brightness, {
      t: 1,
      duration: 1.5,
      ease: 'none',
      onUpdate: () => {
        tmpCol.copy(heroCol).lerp(scrolledCol, brightness.t);
        modelMaterials.forEach((material) => material.color.copy(tmpCol));
      }
    }, 'feature1');

    const modelOpacity = { t: 0 };
    timeline.to(modelOpacity, {
      t: 1,
      duration: 1,
      ease: 'none',
      onUpdate: () => {
        const opacity = MODEL_OPACITY_HERO + (MODEL_OPACITY_SCROLLED - MODEL_OPACITY_HERO) * modelOpacity.t;
        modelMaterials.forEach((material) => {
          material.opacity = opacity;
        });
      }
    }, 'feature1');

  }

  function fitCameraToViewport() {
    if (!camera) return;

    const aspect = window.innerWidth / canvasHeight();
    const fullFitRatio = aspect < DESIGN_ASPECT ? DESIGN_ASPECT / aspect : 1;
    const ratio = 1 + (fullFitRatio - 1) * MOBILE_ZOOM_STRENGTH;
    const isMobile = window.innerWidth < 750;
    camera.position.z = 5 * ratio - (isMobile ? MOBILE_EXTRA_ZOOM : 0);
    camera.aspect = aspect;
    camera.updateProjectionMatrix();
  }

  function resizeRenderer() {
    if (!renderer || !camera || isContextLost || isDisposed) return;

    fitCameraToViewport();
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(window.innerWidth, canvasHeight());

    window.requestAnimationFrame(() => {
      if (isDisposed || isContextLost) return;
      ScrollTrigger.refresh();
      renderer.render(scene, camera);
    });
  }

  function syncScrollState() {
    if (!timeline?.scrollTrigger || isDisposed || isContextLost) return;

    timeline.scrollTrigger.refresh();
    ScrollTrigger.refresh();
    ScrollTrigger.update();
    timeline.progress(timeline.scrollTrigger.progress, false);
  }

  function handleResize() {
    const widthDelta = Math.abs(window.innerWidth - lastResizeWidth);
    const heightDelta = Math.abs(window.innerHeight - lastResizeHeight);
    if (!widthDelta && heightDelta < 80) return;

    lastResizeWidth = window.innerWidth;
    lastResizeHeight = window.innerHeight;
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(resizeRenderer, 120);
  }

  function startRenderLoop() {
    function animate() {
      if (isDisposed || isContextLost) return;
      animationFrameId = requestAnimationFrame(animate);
      renderer.render(scene, camera);
    }

    animate();
  }

  function warmUpRenderer() {
    fitCameraToViewport();
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(window.innerWidth, canvasHeight());
    syncScrollState();
    renderer.compile(scene, camera);
    renderer.render(scene, camera);
  }

  function markReady() {
    syncScrollState();
    canvas.classList.remove('is-loading', 'is-recovering');
    canvas.classList.add('is-ready');
    renderer.render(scene, camera);
  }

  function setupContextRecovery() {
    const handleContextLost = (event) => {
      event.preventDefault();
      debugLog('WebGL context lost');
      isContextLost = true;
      canvas.classList.remove('is-ready');
      canvas.classList.add('is-recovering');
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
    };

    const handleContextRestored = () => {
      debugLog('WebGL context restored');
      window.setTimeout(initScrollytelling, 100);
    };

    canvas.addEventListener('webglcontextlost', handleContextLost, false);
    canvas.addEventListener('webglcontextrestored', handleContextRestored, false);
    cleanupFns.push(() => {
      canvas.removeEventListener('webglcontextlost', handleContextLost, false);
      canvas.removeEventListener('webglcontextrestored', handleContextRestored, false);
    });
  }

  function setupHeaderAnchorScrolling() {
    const anchors = document.querySelectorAll('header a[href^="#"]');
    anchors.forEach((anchor) => {
      const handleClick = (event) => {
        event.preventDefault();
        const targetEl = document.querySelector(anchor.getAttribute('href'));
        if (targetEl) window.scrollTo({ top: targetEl.offsetTop, behavior: 'smooth' });
      };

      anchor.addEventListener('click', handleClick);
      cleanupFns.push(() => anchor.removeEventListener('click', handleClick));
    });
  }

  function setupHeroCta() {
    const heroPill = document.querySelector('[data-hero-cta]');
    if (!heroPill) return;

    const ownSection = heroPill.closest('.shopify-section');
    const buyingSection = ownSection?.nextElementSibling;
    const buyingControlsSelector = [
      '.product-form-buttons',
      '.shopify-payment-button',
      '[id^="BuyButtons-ProductSubmitButton-"]',
      '.buy-buttons-block',
      'product-form-component'
    ].join(', ');

    function getBuyingScrollTarget() {
      return buyingSection?.querySelector(buyingControlsSelector) || buyingSection;
    }

    function clampScrollTop(scrollTop) {
      const maxScrollTop = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      return Math.min(Math.max(0, scrollTop), maxScrollTop);
    }

    const handleClick = () => {
      const scrollTarget = getBuyingScrollTarget();
      if (!scrollTarget) return;

      const headerHeight = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
      const targetTop = window.scrollY + scrollTarget.getBoundingClientRect().top;
      const preferredOffset = window.innerHeight * 0.62;
      const overlapSafeOffset = headerHeight + 16;
      const targetOffset = Math.max(preferredOffset, overlapSafeOffset);

      window.scrollTo({
        top: clampScrollTop(targetTop - targetOffset),
        behavior: 'smooth'
      });
    };

    heroPill.addEventListener('click', handleClick);
    cleanupFns.push(() => heroPill.removeEventListener('click', handleClick));

    if (buyingSection && 'IntersectionObserver' in window) {
      buyingSection.classList.add('scrollytelling-buy-section');
      const buyingControls = getBuyingScrollTarget();
      let ctaUpdateFrame;

      function updateHeroPillVisibility() {
        ctaUpdateFrame = undefined;
        const controlsRect = buyingControls.getBoundingClientRect();
        const sectionRect = buyingSection.getBoundingClientRect();
        const hasReachedLowerPage = sectionRect.top <= window.innerHeight;
        const hasReachedBuyingControls = controlsRect.top <= window.innerHeight;
        canvas.classList.toggle('is-faded-out', hasReachedLowerPage);
        heroPill.classList.toggle('is-faded', hasReachedBuyingControls);
        heroPill.toggleAttribute('disabled', hasReachedBuyingControls);
      }

      function scheduleHeroPillVisibilityUpdate() {
        if (ctaUpdateFrame) return;
        ctaUpdateFrame = window.requestAnimationFrame(updateHeroPillVisibility);
      }

      const sectionObserver = new IntersectionObserver((entries) => {
        const entry = entries[0];
        if (!entry) return;
        buyingSection.classList.toggle('is-faded-in', entry.isIntersecting);
      }, { threshold: 0 });

      sectionObserver.observe(buyingSection);
      cleanupFns.push(() => sectionObserver.disconnect());

      updateHeroPillVisibility();
      window.addEventListener('scroll', scheduleHeroPillVisibilityUpdate, { passive: true });
      window.addEventListener('resize', scheduleHeroPillVisibilityUpdate);
      cleanupFns.push(() => {
        window.removeEventListener('scroll', scheduleHeroPillVisibilityUpdate);
        window.removeEventListener('resize', scheduleHeroPillVisibilityUpdate);
        if (ctaUpdateFrame) cancelAnimationFrame(ctaUpdateFrame);
      });
    }
  }

  function disposeObject(object) {
    if (!object) return;

    object.traverse((child) => {
      if (child.geometry) child.geometry.dispose();
      if (!child.material) return;

      const materials = Array.isArray(child.material) ? child.material : [child.material];
      materials.forEach((material) => {
        Object.keys(material).forEach((key) => {
          const value = material[key];
          if (value && typeof value.dispose === 'function') value.dispose();
        });
        material.dispose();
      });
    });
  }

  function teardown() {
    isDisposed = true;
    window.clearTimeout(resizeTimer);
    if (animationFrameId) cancelAnimationFrame(animationFrameId);
    cleanupFns.splice(0).forEach((cleanup) => cleanup());
    window.removeEventListener('resize', handleResize);

    if (timeline) {
      if (timeline.scrollTrigger) timeline.scrollTrigger.kill();
      timeline.kill();
    }

    disposeObject(scene);

    if (studioEnv) studioEnv.dispose();
    if (pmrem) pmrem.dispose();
    if (renderer) {
      renderer.dispose();
    }

    if (canvas.__scrollytellingController?.teardown === teardown) {
      delete canvas.__scrollytellingController;
    }
  }

  async function boot() {
    try {
      setupContextRecovery();
      setupHeaderAnchorScrolling();
      setupHeroCta();

      const assets = await loadAssets();
      if (isDisposed) return;

      createScene(assets);
      fitCameraToViewport();
      setupAnimations();
      warmUpRenderer();
      startRenderLoop();
      markReady();

      window.addEventListener('resize', handleResize);
      cleanupFns.push(() => window.removeEventListener('resize', handleResize));
    } catch (error) {
      debugLog('Scrollytelling failed to initialize', error);
      canvas.classList.remove('is-ready', 'is-loading');
      canvas.classList.add('is-recovering');
    }
  }

  boot();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initScrollytelling);
} else {
  initScrollytelling();
}
